import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGet, usePost } from "@/api/useApi";
import { fetchClient } from "@/api/fetchClient";
import { toastSuccess } from "@/lib/Toast";
import i18n from "@/i18n";
import { normalizeClientRow } from "../utils/normalizeClientRow";

const ACTION_TOAST_KEYS = {
  block: {
    header: "clientsBoard.toast.blocked",
    description: "clientsBoard.toast.blockedDesc",
  },
  unblock: {
    header: "clientsBoard.toast.unblocked",
    description: "clientsBoard.toast.unblockedDesc",
  },
  deactivate: {
    header: "clientsBoard.toast.deactivated",
    description: "clientsBoard.toast.deactivatedDesc",
  },
  reactivate: {
    header: "clientsBoard.toast.reactivated",
    description: "clientsBoard.toast.reactivatedDesc",
  },
  promote: {
    header: "clientsBoard.toast.activated",
    description: "clientsBoard.toast.activatedDesc",
  },
};


export function useAgencyClientsSearch({
  companyId,
  agencyId,
  filterKey,
  page,
  pageSize,
  enabled,
  companyName,
}) {
  const params = {
    resource: "companies",
    filter_key: filterKey,
    page: String(page),
    page_size: String(pageSize),
  };

  if (companyName) {
    params.company_name = companyName;
  }

  const url =
    companyId && agencyId
      ? `companies/${companyId}/search/agencies/${agencyId}`
      : "companies/_/search/agencies/_";

  return useGet("agency-clients", url, params, {
    enabled: Boolean(enabled) && Boolean(companyId) && Boolean(agencyId),
  });
}


export async function fetchAllAgencyClients({
  companyId,
  agencyId,
  filterKey,
  companyName,
  total = 0,
}) {
  if (!companyId || !agencyId) return [];

  const params = {
    resource: "companies",
    filter_key: filterKey,
    page: "1",
    page_size: String(total > 0 ? total : 100000),
  };
  if (companyName) params.company_name = companyName;

  const res = await fetchClient(
    `companies/${companyId}/search/agencies/${agencyId}`,
    { params },
  );
  const items = res?.data?.items ?? [];
  return items.map((item) => normalizeClientRow(item));
}

export function useAgencyClientProfile(agencyId, clientId) {
  return useGet(
    "agency-client",
    `agencies/${agencyId}/clients/${clientId}`,
    {},
    {
      enabled: Boolean(agencyId && clientId),
      select: (res) => res?.data ?? null,
    },
  );
}

export function useAgencyClientRoles(agencyId, clientId) {
  return useGet(
    "agency-client-roles",
    `agencies/${agencyId}/clients/${clientId}/roles`,
    {},
    {
      enabled: Boolean(agencyId && clientId),
      select: (res) =>
        res?.data?.items ?? (Array.isArray(res?.data) ? res.data : []),
    },
  );
}

export function useAgencyClientBranches(agencyId, clientId) {
  return useGet(
    "agency-client-branches",
    `agencies/${agencyId}/clients/${clientId}/branches`,
    {},
    {
      enabled: Boolean(agencyId && clientId),
      select: (res) =>
        res?.data?.items ?? (Array.isArray(res?.data) ? res.data : []),
    },
  );
}


export function useCreateAgencyClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agencyId, body }) =>
      fetchClient(`agencies/${agencyId}/clients`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-clients"] });
      toastSuccess({ header: "Client company created" });
    },
  });
}

export function usePutAgencyClientProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agencyId, clientId, body }) =>
      fetchClient(`agencies/${agencyId}/clients/${clientId}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-clients"] });
      queryClient.invalidateQueries({ queryKey: ["agency-client"] });
      toastSuccess({ header: "Client updated" });
    },
  });
}


export function useClientActions(agencyId, { successHeader } = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, action, body = {} }) => {
      const url = `agencies/${agencyId}/clients/${clientId}?action=${encodeURIComponent(
        action,
      )}`;
      return fetchClient(url, { method: "PUT", body });
    },
    onSuccess: (_res, { action } = {}) => {
      queryClient.invalidateQueries({ queryKey: ["agency-clients"] });
      if (successHeader) {
        toastSuccess({ header: successHeader });
        return;
      }
      const entry = ACTION_TOAST_KEYS[action];
      toastSuccess({
        header: entry ? i18n.t(entry.header) : i18n.t("clientsBoard.toast.saved"),
        description: entry ? i18n.t(entry.description) : "",
      });
    },
  });
}

export function useInviteAgencyClientUser() {
  const queryClient = useQueryClient();

  return usePost(`../agencies/invite-user-to-org`, {
    onSuccess: (_res, body) => {
      queryClient.invalidateQueries({
        queryKey: ["agency-client-users", body?.company_id],
      });
      toastSuccess({ header: "User invited" });
    },
  });
}
